import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from '../../../../group_leader/components/common'
import { makeT } from '../../../../group_leader/helpers/i18n'
import AmPmTimePicker from './AmPmTimePicker'
import TeeTimeTypeOption from './TeeTimeTypeOption'
import { GROUP_SIZES } from '../../../helpers/pairings'

const t = makeT('group_leader.event_setup.pairings')

// Which plural the group-size hint uses; anything else reads as sixsomes.
const SIZE_HINTS = { 2: 'twosomes', 3: 'threesomes', 4: 'foursomes', 5: 'fivesomes' }
const groupSizeHint = (size) => t(`group_size.${SIZE_HINTS[size] || 'sixsomes'}`)

const START_TYPES = [ [ 'teetimes', 'sequential_label' ], [ 'shotgun', 'shotgun_label' ], [ 'walkon', 'walkon_label' ] ]

// Presentational: every value and handler comes from PairingsStep; the schedule swaps with the type.
const PairingsConfigPanel = ({
  open, onToggle, summary, groupSize, onGroupSizeChange, isWalkon, teamGamePresent, locked,
  schedule, courseName, holeCount, canApply,
}) => {
  // Collapsed by default: the group size is almost always left at four.
  const [ sizeOpen, setSizeOpen ] = useState(false)
  const sizeHint = isWalkon ? t('group_size.walkon_hint') : groupSizeHint(groupSize)

  return (
  <section className="gl-event-setup-config" aria-labelledby="pair-config-title">
    <button
      type="button"
      className="gl-event-setup-config-hd"
      aria-expanded={open}
      aria-controls="pair-config-body"
      onClick={onToggle}
    >
      <span className="gl-event-setup-config-hd-text">
        <span id="pair-config-title" className="gl-event-setup-config-title">{t('config.title')}</span>
        {open
          ? <span className="gl-event-setup-config-explain">{t('config.explain')}</span>
          : <span className="gl-event-setup-config-summary">{summary}</span>}
      </span>
      <span className={`gl-event-setup-config-chevron${open ? ' is-open' : ''}`} aria-hidden="true">&#8964;</span>
    </button>

    {open && (
      <div id="pair-config-body" className="gl-event-setup-config-body">
        <div className="gl-event-setup-gs-block">
          <button
            type="button"
            className="gl-event-setup-gs-hd"
            aria-expanded={sizeOpen}
            aria-controls="pair-gs-body"
            onClick={() => setSizeOpen((wasOpen) => !wasOpen)}
          >
            <span className="gl-event-setup-gs-title">{t('group_size.title')}</span>
            <span className="gl-event-setup-gs-hint">{sizeHint}</span>
            <span className={`gl-event-setup-gs-chevron${sizeOpen ? ' is-open' : ''}`} aria-hidden="true">&#8964;</span>
          </button>
          {sizeOpen && (
            <div id="pair-gs-body" className="gl-event-setup-gs-body">
              <div className="gl-event-setup-seg-group" role="group" aria-label={t('group_size.aria_label')}>
                {GROUP_SIZES.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`gl-event-setup-seg-btn${!isWalkon && groupSize === n ? ' is-active' : ''}`}
                    aria-pressed={!isWalkon && groupSize === n}
                    disabled={locked || isWalkon}
                    aria-disabled={locked || isWalkon}
                    onClick={() => onGroupSizeChange(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="gl-event-setup-tt-block">
          <div className="gl-event-setup-tt-block-hd">
            <span className="gl-event-setup-tt-block-title">{t('tee_times.title')}</span>
          </div>

          <div className="gl-event-setup-tt-seg" role="radiogroup" aria-label={t('tee_times.aria_label')}>
            {/* A team game takes its teams from the sheet, so walk-on is not on offer. */}
            {START_TYPES.filter(([ value ]) => value !== 'walkon' || !teamGamePresent).map(([ value, label ]) => (
              <TeeTimeTypeOption
                key={value}
                value={value}
                checked={schedule.type === value}
                onChange={schedule.onTypeChange}
                disabled={locked}
                name={t(`tee_times.${label}`)}
              />
            ))}
          </div>

          {/* One row: the run's time, its second field, the shared starting hole, and Apply. */}
          {schedule.type !== 'walkon' && (
            <div className={`gl-event-setup-tt-sub-fields is-${schedule.type}`}>
              {schedule.type === 'teetimes' ? (
                <>
                  <div className="gl-event-setup-tt-cf-group is-phone-full">
                    <label className="gl-event-setup-tt-cf-label" htmlFor="pair-tt-time">{t('tee_times.first_tee_time')}</label>
                    <AmPmTimePicker id="pair-tt-time" value={schedule.teeTime} onChange={schedule.setTeeTime}/>
                  </div>
                  <div className="gl-event-setup-tt-cf-group is-w-md">
                    <label className="gl-event-setup-tt-cf-label" htmlFor="pair-tt-interval">{t('tee_times.interval')}</label>
                    {/* Typed, so any interval works; the unit sits beside it, as the field always holds a value. */}
                    <div className="gl-event-setup-tt-cf-unit">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="gl-event-setup-tt-cf-input"
                        id="pair-tt-interval"
                        value={schedule.interval}
                        onChange={(e) => schedule.setInterval(e.target.value.replace(/[^\d]/g, ''))}
                      />
                      {/* Inside the box and not editable: the unit is fixed, only the number is typed. */}
                      <span className="gl-event-setup-tt-cf-unit-label" aria-hidden="true">
                        {t('tee_times.interval_unit')}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="gl-event-setup-tt-cf-group is-phone-full">
                    <label className="gl-event-setup-tt-cf-label" htmlFor="pair-tt-sg-time">{t('tee_times.start_time')}</label>
                    <AmPmTimePicker id="pair-tt-sg-time" value={schedule.shotgunTime} onChange={schedule.setShotgunTime}/>
                  </div>
                  <div className="gl-event-setup-tt-cf-group is-w-lg">
                    <label className="gl-event-setup-tt-cf-label" htmlFor="pair-tt-sg-assign">{t('tee_times.hole_assignment')}</label>
                    <select
                      className="gl-event-setup-tt-cf-select"
                      id="pair-tt-sg-assign"
                      value={schedule.holeAssignment}
                      onChange={(e) => schedule.setHoleAssignment(e.target.value)}
                    >
                      <option value="sequential">{t('tee_times.auto_assign')}</option>
                      <option value="reverse">{t('tee_times.reverse')}</option>
                    </select>
                  </div>
                </>
              )}
              {/* As wide as its one-line label, which names the course; the field keeps to two digits. */}
              <div className="gl-event-setup-tt-cf-group">
                <label className="gl-event-setup-tt-cf-label" htmlFor="pair-tt-hole">
                  {courseName ? t('tee_times.starting_hole_on', { course: courseName }) : t('tee_times.starting_hole')}
                </label>
                <div className="gl-event-setup-tt-hole-field">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="gl-event-setup-tt-cf-input"
                    id="pair-tt-hole"
                    value={schedule.startHole}
                    onChange={(e) => schedule.onStartHoleChange(e.target.value)}
                    placeholder={t('tee_times.hole_range', { max: holeCount })}
                  />
                </div>
              </div>
              <div className="gl-event-setup-tt-cf-group gl-event-setup-tt-apply-cell">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => schedule.onApply()}
                  disabled={schedule.applying || !canApply}
                >
                  {schedule.applying ? t('tee_times.applying') : t('tee_times.apply')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
  </section>
  )
}

PairingsConfigPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  // The collapsed header's one-line recap, built by the step from the same values.
  summary: PropTypes.string.isRequired,
  groupSize: PropTypes.string.isRequired,
  onGroupSizeChange: PropTypes.func.isRequired,
  isWalkon: PropTypes.bool.isRequired,
  teamGamePresent: PropTypes.bool.isRequired,
  // True while a reorganize runs: the controls stay visible but inert.
  locked: PropTypes.bool.isRequired,
  schedule: PropTypes.shape({
    type: PropTypes.oneOf([ 'teetimes', 'shotgun', 'walkon' ]).isRequired,
    onTypeChange: PropTypes.func.isRequired,
    teeTime: PropTypes.string,
    setTeeTime: PropTypes.func.isRequired,
    interval: PropTypes.string,
    setInterval: PropTypes.func.isRequired,
    startHole: PropTypes.string,
    onStartHoleChange: PropTypes.func.isRequired,
    shotgunTime: PropTypes.string,
    setShotgunTime: PropTypes.func.isRequired,
    holeAssignment: PropTypes.oneOf([ 'sequential', 'reverse' ]).isRequired,
    setHoleAssignment: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    applying: PropTypes.bool,
  }).isRequired,
  // Named in the starting-hole label, and its hole count bounds the field.
  courseName: PropTypes.string,
  holeCount: PropTypes.number.isRequired,
  // False on an empty sheet: there is nothing for Apply to write to yet.
  canApply: PropTypes.bool.isRequired,
}

export default PairingsConfigPanel
